import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { doc, updateDoc, deleteField, serverTimestamp } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import {
  FaMusic,
  FaUpload,
  FaUndo,
  FaSpinner,
  FaFileAudio,
} from "react-icons/fa";
import { db, storage } from "../firebase/firebase";
import BlockDisplayComponent from "../BlockDisplayComponent/BlockDisplayComponent";
import "./SongChangeComponent.scss";

const MAX_FILE_SIZE_MB = 15;

const SongChangeComponent = () => {
  const navigate = useNavigate();
  const { slug, weddingData } = useOutletContext();
  const fileInputRef = useRef(null);

  const isPremium = weddingData?.plan === "premium";
  const musicChangeEnabled =
    isPremium && weddingData?.addons?.musicChange === true;

  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [localMusicUrl, setLocalMusicUrl] = useState("");

  const currentMusicUrl = useMemo(() => {
    return localMusicUrl || weddingData?.musicUrl || "";
  }, [localMusicUrl, weddingData]);

  const validateAndSetFile = (file) => {
    if (!file) return;

    const isMp3 =
      file.type === "audio/mpeg" ||
      file.type === "audio/mp3" ||
      file.name.toLowerCase().endsWith(".mp3");

    if (!isMp3) {
      setSelectedFile(null);
      setMessage("Please upload only an MP3 file.");
      return;
    }

    const fileSizeMb = file.size / (1024 * 1024);

    if (fileSizeMb > MAX_FILE_SIZE_MB) {
      setSelectedFile(null);
      setMessage(`File size must be less than ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }

    setSelectedFile(file);
    setUploadProgress(0);
    setMessage("");
  };

  const handleFileChange = (e) => {
    validateAndSetFile(e.target.files?.[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!musicChangeEnabled || isSaving) return;

    setIsDragging(false);
    validateAndSetFile(e.dataTransfer.files?.[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!musicChangeEnabled || isSaving) return;

    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const getFirebaseErrorMessage = (error) => {
    if (error?.code === "storage/unauthorized") {
      return "Upload failed. Firebase Storage permission is denied.";
    }

    if (error?.code === "storage/canceled") {
      return "Upload was cancelled.";
    }

    if (error?.code === "storage/retry-limit-exceeded") {
      return "Upload failed due to network issue. Please try again.";
    }

    return error?.message || "Failed to upload music. Please try again.";
  };

  const handleUpload = () => {
    if (!musicChangeEnabled) {
      setMessage("This add-on is not enabled for this package.");
      return;
    }

    if (!slug) {
      setMessage("Site slug is missing. Please refresh and try again.");
      return;
    }

    if (!selectedFile) {
      setMessage("Please select an MP3 file first.");
      return;
    }

    setIsSaving(true);
    setUploadProgress(0);
    setMessage("");

    const safeFileName = `${Date.now()}-${selectedFile.name.replace(
      /[^a-zA-Z0-9.-]/g,
      "_"
    )}`;

    const musicRef = ref(storage, `sites/${slug}/music/${safeFileName}`);

    const uploadTask = uploadBytesResumable(musicRef, selectedFile, {
      contentType: "audio/mpeg",
    });

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setUploadProgress(progress);
      },
      (error) => {
        console.error("Music upload failed:", error.code, error.message, error);
        setIsSaving(false);
        setMessage(getFirebaseErrorMessage(error));
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

          await updateDoc(doc(db, "sites", slug), {
            musicUrl: downloadUrl,
            musicPath: uploadTask.snapshot.ref.fullPath,
            musicFileName: selectedFile.name,
            musicUpdatedAt: serverTimestamp(),
          });

          setLocalMusicUrl(downloadUrl);
          setSelectedFile(null);
          setUploadProgress(100);
          setMessage("Music updated successfully.");

          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        } catch (error) {
          console.error("Music save failed:", error);
          setMessage(error?.message || "Music uploaded, but failed to save URL.");
        } finally {
          setIsSaving(false);
        }
      }
    );
  };

  const handleResetDefault = async () => {
    if (!musicChangeEnabled) {
      setMessage("This add-on is not enabled for this package.");
      return;
    }

    if (!slug) {
      setMessage("Site slug is missing. Please refresh and try again.");
      return;
    }

    try {
      setIsSaving(true);
      setUploadProgress(0);
      setMessage("");

      await updateDoc(doc(db, "sites", slug), {
        musicUrl: deleteField(),
        musicPath: deleteField(),
        musicFileName: deleteField(),
        musicUpdatedAt: serverTimestamp(),
      });

      setLocalMusicUrl("");
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setMessage("Default music restored successfully.");
    } catch (error) {
      console.error("Default music reset failed:", error);
      setMessage(error?.message || "Failed to restore default music.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!musicChangeEnabled) {
    return (
      <BlockDisplayComponent
        title="This feature is locked"
        description="Premium users can access it only when this add-on has been added to their package. Call 070 536 7980 to get this feature"
        primaryButtonText="Add this feature"
        secondaryButtonText="Go Back"
        onPrimaryClick={() => navigate("/premium")}
        onSecondaryClick={() => navigate(-1)}
      />
    );
  }

  return (
    <div className="songChange">
      <div className="songChange__header">
        <div className="songChange__icon">
          <FaMusic />
        </div>

        <div>
          <h2 className="songChange__title">Change Music</h2>
          <p className="songChange__subtitle">
            Upload an MP3 file as the background music of your digital invitation.
          </p>
        </div>
      </div>

      <div className="songChange__card">
        <label
          className={`songChange__uploadBox ${
            isDragging ? "is-dragging" : ""
          } ${selectedFile ? "has-file" : ""}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/mpeg,audio/mp3,.mp3"
            onChange={handleFileChange}
            disabled={isSaving}
          />

          <span className="songChange__uploadIcon">
            {selectedFile ? <FaFileAudio /> : <FaUpload />}
          </span>

          <span className="songChange__uploadTitle">
            {selectedFile ? selectedFile.name : "Drag & drop your MP3 file here"}
          </span>

          <span className="songChange__uploadText">
            or click here to browse. Maximum file size: {MAX_FILE_SIZE_MB}MB.
          </span>
        </label>

        {isSaving && (
          <div className="songChange__progressWrap">
            <div className="songChange__progressInfo">
              <span>Uploading music...</span>
              <strong>{uploadProgress}%</strong>
            </div>

            <div className="songChange__progressBar">
              <span style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        {currentMusicUrl ? (
          <div className="songChange__preview">
            <p className="songChange__previewLabel">Current custom music</p>
            <audio controls src={currentMusicUrl} />
          </div>
        ) : (
          <div className="songChange__defaultNotice">
            Default invitation music is currently active.
          </div>
        )}

        {message && <p className="songChange__message">{message}</p>}

        <div className="songChange__actions">
          <button
            type="button"
            className="songChange__button"
            onClick={handleUpload}
            disabled={!selectedFile || isSaving}
          >
            {isSaving ? <FaSpinner className="songChange__spin" /> : <FaUpload />}
            {isSaving ? "Uploading..." : "Upload Music"}
          </button>

          <button
            type="button"
            className="songChange__button songChange__button--ghost"
            onClick={handleResetDefault}
            disabled={isSaving}
          >
            <FaUndo />
            Change to Default Music
          </button>
        </div>
      </div>
    </div>
  );
};

export default SongChangeComponent;